import type { CarePlan, Goal } from '@medplum/fhirtypes'
import { codeableConcept } from '#src/codeableConcept.ts'
import { CARE_PLAN_CATEGORY } from '#src/codeSystems.ts'
import { recordEntries } from '#src/utils.ts'

export const TOP_LEVEL_CARE_PLAN_CATEGORY = codeableConcept({
  system: CARE_PLAN_CATEGORY,
  code: 'care-plan-initial',
  display: 'Initial Care Plan',
})

export const GOAL_CARE_PLAN_CATEGORY = codeableConcept({
  system: CARE_PLAN_CATEGORY,
  code: 'care-plan-goal',
  display: 'Goal',
})

export const INTERVENTION_CARE_PLAN_CATEGORY = codeableConcept({
  system: CARE_PLAN_CATEGORY,
  code: 'care-plan-intervention',
  display: 'Intervention',
})

export const OUTCOME_STATUS = {
  proposed: 'Draft',
  active: 'Active',
  completed: 'Achieved',
  'on-hold': 'Extended',
  cancelled: 'Voided',
} satisfies Record<
  Exclude<
    Goal['lifecycleStatus'],
    'planned' | 'accepted' | 'entered-in-error' | 'rejected'
  >,
  string
>

export type OutcomeStatus = keyof typeof OUTCOME_STATUS

export const INTERVENTION_STATUS = {
  draft: 'Draft',
  active: 'In Progress',
  completed: 'Completed',
  'on-hold': 'In Progress - Off Track',
  revoked: 'Discontinued',
  'entered-in-error': 'Deleted',
} satisfies Record<Exclude<CarePlan['status'], 'unknown'>, string>

export type InterventionStatus = keyof typeof INTERVENTION_STATUS

export const CARE_PLAN_STATUS = {
  draft: 'Draft',
  active: 'Active',
  'on-hold': 'Approved',
  completed: 'Closed',
  revoked: 'Voided',
  'entered-in-error': 'Deleted',
} satisfies Record<Exclude<CarePlan['status'], 'unknown'>, string>

/* The statuses that put a care plan behind the participant rather than in front
 * of them — what `CARE_PLAN_STATUS` above labels Closed, Voided and Deleted.
 * This is the set every `queryOpenByParticipant` subtracts, named once so the
 * open/closed line is drawn in one place.
 *
 * Status, deliberately, and not `CarePlanState.Closed`. A state is the
 * `(status, intent)` pair `['completed', 'order']`, so a plan whose status was
 * written without its intent reads back as `CarePlanState.Unknown` while being
 * every bit as closed — a shape that is not hypothetical, it is a fifth of the
 * resolvable plans on dev. Filtering on state would let those through. */
export const CLOSED_CARE_PLAN_STATUSES: CarePlan['status'][] = [
  'completed',
  'revoked',
  'entered-in-error',
]

export type CarePlanStatus = keyof typeof CARE_PLAN_STATUS

export const CarePlanState = {
  Draft: 'Draft',
  PendingSignatures: 'Pending Signatures',
  SignedOff: 'Signed Off',
  Active: 'Active',
  Closed: 'Closed',
  Voided: 'Voided',
  Deleted: 'Deleted',
  Unknown: 'Unknown',
} as const
export type CarePlanState = (typeof CarePlanState)[keyof typeof CarePlanState]

export const INITIAL_CARE_PLAN_STATUS_OPTIONS = recordEntries(CarePlanState)
  // don't show deleted status
  .filter(([key]) => key !== 'Deleted' && key !== 'Unknown')
  .map(([_, value]) => ({
    value,
    label: value,
  }))

export const CARE_PLAN_STATUS_OPTIONS = recordEntries(CARE_PLAN_STATUS)
  // don't show deleted status
  .filter(([key]) => key !== 'entered-in-error')
  .map(([key, value]) => ({
    value: key,
    label: value,
  }))
